import _ from 'underscore';
import { OverridableToolbarView } from '@bingads-webui/toolbar';

import { constants } from './constants';

const { TOOLBAR_ROW_TYPE } = constants;

export class GridToolbarGroup {
  /**
   * A manager for all toolbars with in a grid. It connects a grid with a set of
   * toolbars. The toolbars are created and owned by the `GridToolbarGroup`, and
   * will be removed on `GridToolbarGroup#remove()`.
   * @param {Object} options -
   * @param {GridView} options.gridView - The grid view containing the toolbars
   * @param {Object.<string, Object>} options.toolbars -
   *    A hash of the options to create the toolbars. The keys are the names of
   *    the toolbars. The values are the options to construct `OverridableToolbarView`s
   */
  constructor({ gridView, toolbars }) {
    this.toolbars = _.mapObject(toolbars, options => new OverridableToolbarView(options).render());
    this.gridView = gridView;

    let scheduledUpdate = null;
    _.each(this.toolbars, toolbar => toolbar.on('visibilityChanged', () => {
      if (!scheduledUpdate) {
        scheduledUpdate = _.defer(() => {
          scheduledUpdate = null;
          this.updateGridView();
        });
      }
    }));
  }

  /**
   * Query the names of all managed toolbars
   * @returns {string[]} - The names of all managed toolbars
   */
  toolbarNames() {
    return _.keys(this.toolbars);
  }

  /**
   * Query the toolbar by name
   * @param {string} name - The name of the toolbar
   * @returns {OverridableToolbarView} - The toolbar view with the given name
   */
  toolbarWithName(name) {
    return _.result(this.toolbars, name, null);
  }

  /**
   * Query the toolbar containing a certain toolbar item
   * @param {string} id - The id of the toolbar item
   * @returns {OverridableToolbarView} - The toolbar view containing the item
   */
  toolbarWithItem(id) {
    return _.find(this.toolbars, toolbar => Boolean(toolbar.get(id)));
  }

  /**
   * Hide all toolbars
   * @returns {void}
   */
  hideAllToolbars() {
    _.each(this.toolbars, toolbar => toolbar.hide());
  }

  /**
   * Show all toolbars
   * @returns {void}
   */
  showAllToolbars() {
    _.each(this.toolbars, toolbar => toolbar.show());
  }

  /**
   * Update the associated grid view with toolbar changes
   * @returns {void}
   */
  updateGridView() {
    this.gridView.setHeadRows(rows => _.map(rows, (row) => {
      if (!_.isObject(row)) {
        return row;
      }

      const { type, name } = row;

      if (type !== TOOLBAR_ROW_TYPE) {
        return row;
      }

      const view = _.result(this.toolbars, name, null);
      const classes = _.union(['grid-toolbar'], _.result(row, 'classes', []));
      // We will preserve most of the attributes except for the conflicting ones
      //  * data-name, we use this to identify the toolbar
      //  * hidden, we use this to control the toolbar visibility
      const attributes = _.defaults({
        'data-name': name,
        hidden: view.isHidden(),
      }, _.result(row, 'attributes', {}));

      return _.defaults({
        type: TOOLBAR_ROW_TYPE,
        name,
        view,
        attributes,
        classes,
      }, row);
    }));
  }

  /**
   * Destroy all toolbar views
   * @returns {void}
   */
  remove() {
    _.each(this.toolbars, toolbar => toolbar.remove());
  }
}
